/**
 * Pure helpers for the receipt review screen (D34): which list row each
 * receipt line pays for, the apply request built from those choices, and the
 * optimistic list update once it succeeds.
 */
import type {
  GroceryItemDto,
  GroceryListDetail,
  ReceiptApplyRequest,
  ReceiptApplyResponse,
  ReceiptScanLine,
  ReceiptScanResponse,
} from '@fewerbags/schema';

/** `item:<id>`, `new` (add as a new list item) or `ignore`. */
export type ReceiptChoice = `item:${string}` | 'new' | 'ignore';

export interface ReceiptLineState {
  choice: ReceiptChoice;
  /** Price input text in dollars. */
  price: string;
  /** Name for a new item ("Add as new item"). */
  newName: string;
}

/** Navigation state carried from the scan to the review page. */
export interface ReceiptNavState {
  proposal?: ReceiptScanResponse;
}

/** Net price of a line: what it charged minus its own discount, never below 0. */
export function lineNetCents(line: Pick<ReceiptScanLine, 'totalCents' | 'discountCents'>): number {
  return Math.max(0, line.totalCents - (line.discountCents ?? 0));
}

/** "organic baby spinach" → "Organic baby spinach" as a starting name for a new item. */
function suggestedName(line: ReceiptScanLine): string {
  const base = line.normalisedText.trim() || line.rawText.trim().toLowerCase();
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/** Pre-filled choices: the suggestion when there is one, otherwise "Ignore". */
export function initialLineStates(proposal: ReceiptScanResponse): ReceiptLineState[] {
  return proposal.lines.map((line) => ({
    choice: line.suggestedItemId ? `item:${line.suggestedItemId}` : 'ignore',
    price: (lineNetCents(line) / 100).toFixed(2),
    newName: suggestedName(line),
  }));
}

export function choiceItemId(choice: ReceiptChoice): string | null {
  return choice.startsWith('item:') ? choice.slice('item:'.length) : null;
}

/**
 * The select's options for one line: its alternatives first, then every other
 * unchecked row, then "Add as new item" and "Ignore". Rows another line has
 * already claimed stay listed (the person may be moving a match) but are marked.
 */
export function lineOptions(
  line: ReceiptScanLine,
  unchecked: readonly GroceryItemDto[],
  claimedBy: ReadonlyMap<string, number>,
): Array<{ group: string; options: Array<{ value: ReceiptChoice; label: string }> }> {
  const byId = new Map(unchecked.map((i) => [i.id, i]));
  const label = (item: GroceryItemDto) => {
    const owner = claimedBy.get(item.id);
    const name = item.productLabel ?? item.ingredientName;
    return owner !== undefined && owner !== line.index ? `${name} (matched to another line)` : name;
  };
  const suggested = [line.suggestedItemId, ...line.alternatives.map((a) => a.itemId)]
    .filter((id): id is string => id !== null && byId.has(id))
    .filter((id, i, all) => all.indexOf(id) === i);
  const rest = unchecked.filter((i) => !suggested.includes(i.id));
  const groups = [];
  if (suggested.length > 0) {
    groups.push({
      group: 'Best matches',
      options: suggested.map((id) => ({
        value: `item:${id}` as const,
        label: label(byId.get(id)!),
      })),
    });
  }
  if (rest.length > 0) {
    groups.push({
      group: 'Other items on the list',
      options: rest.map((i) => ({ value: `item:${i.id}` as const, label: label(i) })),
    });
  }
  groups.push({
    group: 'Not on the list',
    options: [
      { value: 'new' as const, label: 'Add as new item' },
      { value: 'ignore' as const, label: 'Ignore this line' },
    ],
  });
  return groups;
}

/** Row id → index of the first line that claims it. */
export function claimedItems(states: readonly ReceiptLineState[]): Map<string, number> {
  const claimed = new Map<string, number>();
  states.forEach((s, i) => {
    const id = choiceItemId(s.choice);
    if (id && !claimed.has(id)) claimed.set(id, i);
  });
  return claimed;
}

/** Rows chosen by more than one line (the apply would be rejected). */
export function duplicateItemIds(states: readonly ReceiptLineState[]): string[] {
  const seen = new Set<string>();
  const dup = new Set<string>();
  for (const s of states) {
    const id = choiceItemId(s.choice);
    if (!id) continue;
    if (seen.has(id)) dup.add(id);
    seen.add(id);
  }
  return [...dup];
}

function priceCents(text: string): number | null {
  const trimmed = text.trim().replace(/^\$/, '');
  if (trimmed === '') return null;
  const dollars = Number(trimmed);
  if (!Number.isFinite(dollars) || dollars < 0) return null;
  return Math.round(dollars * 100);
}

/** Lines whose price input is not a valid amount (and that are not ignored). */
export function invalidPriceLines(states: readonly ReceiptLineState[]): number[] {
  return states.flatMap((s, i) =>
    s.choice !== 'ignore' && priceCents(s.price) === null ? [i] : [],
  );
}

/** "Check off 12 items · $84.12" — what Apply will do. */
export function applySummary(states: readonly ReceiptLineState[]): {
  count: number;
  cents: number;
} {
  let count = 0;
  let cents = 0;
  for (const s of states) {
    if (s.choice === 'ignore') continue;
    count++;
    cents += priceCents(s.price) ?? 0;
  }
  return { count, cents };
}

/** `YYYY-MM-DD` from a printed date or date-time, or null. */
export function receiptDate(purchasedAt: string | null): string | null {
  const m = purchasedAt ? /^(\d{4}-\d{2}-\d{2})/.exec(purchasedAt) : null;
  return m ? m[1]! : null;
}

/**
 * The apply body for the current choices. Quantities come from the list row
 * (what was planned), since a receipt's "qty" is often a weight; new items are
 * one of each. `keyPrefix` makes a retried apply idempotent. Every line carries
 * its printed text so the server can learn the receipt alias.
 */
export function buildApplyRequest(
  proposal: ReceiptScanResponse,
  states: readonly ReceiptLineState[],
  items: readonly GroceryItemDto[],
  opts: { storeId?: string | null | undefined; keyPrefix: string },
): ReceiptApplyRequest {
  const byId = new Map(items.map((i) => [i.id, i]));
  const matches: ReceiptApplyRequest['matches'] = [];
  const newItems: NonNullable<ReceiptApplyRequest['newItems']> = [];
  states.forEach((s, i) => {
    const line = proposal.lines[i]!;
    const cents = priceCents(s.price) ?? 0;
    const clientKey = `${opts.keyPrefix}-${line.index}`.slice(0, 64);
    const itemId = choiceItemId(s.choice);
    if (itemId) {
      const item = byId.get(itemId);
      if (!item) return;
      const qty =
        item.buyQty > 0 ? item.buyQty : item.neededQty && item.neededQty > 0 ? item.neededQty : 1;
      matches.push({
        itemId,
        qty,
        unit: item.unit,
        priceCents: cents,
        clientKey,
        // Teaches the shared receipt-alias table what this line was (D34).
        rawText: line.rawText.slice(0, 200),
      });
    } else if (s.choice === 'new') {
      const name = s.newName.trim();
      if (!name) return;
      newItems.push({
        rawText: line.rawText.slice(0, 200),
        ingredientName: name.slice(0, 120),
        qty: 1,
        unit: 'count',
        priceCents: cents,
        clientKey,
      });
    }
  });
  const purchasedAt = receiptDate(proposal.receipt.purchasedAt);
  return {
    ...(purchasedAt ? { purchasedAt } : {}),
    ...(opts.storeId !== undefined ? { storeId: opts.storeId } : {}),
    matches,
    newItems,
  };
}

/** Fold an apply response into the cached list detail (rows replaced or added, counts, status). */
export function applyReceiptResult(
  detail: GroceryListDetail,
  result: ReceiptApplyResponse,
): GroceryListDetail {
  const byId = new Map(result.items.map((i) => [i.id, i]));
  const items = detail.items.map((i) => byId.get(i.id) ?? i);
  for (const i of result.items) if (!detail.items.some((d) => d.id === i.id)) items.push(i);
  return {
    ...detail,
    list: {
      ...detail.list,
      status: result.listStatus,
      itemCount: items.length,
      checkedCount: items.filter((i) => i.checkedAt !== null).length,
    },
    items,
  };
}
